import React, { useEffect, useMemo, useRef, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTrash } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AdminPopup, DeleteConfirmation, ExportData } from '../../../components/admin';
import GradientStrip from '../../../components/gradientStrip';
import { formatRegistrationListItem } from './registrationListData';
import {
  adminApi,
  exportData,
  getRegistrationsList,
  deleteRegistration,
  getRegistrationDetails,
} from '../../../service';
import { AdminContext } from '../../../contexts/admin.context';

// Note: Column definitions are now dynamically generated based on admin permissions

// Note: Filterable columns are now calculated inside the component based on dynamic columns

// Add highlightMatch function to highlight the matching text
const highlightMatch = (text, query, isNameColumn = false) => {
  if (!query || !text) return text;

  try {
    const queryLower = query.toLowerCase();
    const textLower = text.toLowerCase();
    const index = textLower.indexOf(queryLower);

    if (index === -1) return text;

    const before = text.substring(0, index);
    const match = text.substring(index, index + query.length);
    const after = text.substring(index + query.length);

    // Use different styling for name column (dark background) vs other columns
    const highlightClass = isNameColumn
      ? 'bg-yellow-200 text-gray-800 font-medium'
      : 'bg-yellow-200 font-medium';

    return (
      <>
        {before}
        <span className={highlightClass}>{match}</span>
        {after}
      </>
    );
  } catch (error) {
    console.error('Error highlighting text:', error);
    return text;
  }
};

const AdminRegistrations = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { adminData } = useContext(AdminContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [registrationData, setRegistrationData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  });
  const [fullData, setFullData] = useState([]); // Store all data for frontend pagination

  // Filter states
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState([]);
  const [selectedColumn, setSelectedColumn] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const [uniqueValuesMap, setUniqueValuesMap] = useState({});
  const filterRef = useRef(null);

  // Add search filter state
  const [searchFilter, setSearchFilter] = useState('');

  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    registrationId: null,
    registrationName: '',
    toEmails: [],
    ccEmails: [],
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);

  // Export popup states
  const exportAbortControllerRef = useRef(null);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [isExportPopupOpen, setIsExportPopupOpen] = useState(false);
  const [selectedExportOptions, setSelectedExportOptions] = useState({
    allData: false,
    schoolTypes: [],
    otherGroupTypes: [],
    experienceTypes: [],
  });

  // Check if admin has delete permission
  const hasDeletePermission = adminData?.specialPermissions?.includes('delete');

  // Get query parameters from URL
  const getQueryParams = () => {
    const params = new URLSearchParams(location.search);
    return {
      registrationType: params.get('registrationType') || 'all',
      type: params.get('type') || 'all',
    };
  };

  // Get formatted title based on registration type
  const getRegistrationTypeTitle = (type) => {
    switch (type) {
      case 'school':
        return 'Scolaire';
      case 'other-group':
        return 'Autre Groupe';
      default:
        return 'Tous les types';
    }
  };

  // Get subtitle based on type
  const getSubtitle = (type) => {
    switch (type) {
      case 'school':
        return 'Inscriptions établis. (College)';
      case 'other-group':
        return 'Inscriptions groupes';
      default:
        return 'Tous les inscriptions';
    }
  };

  // Fetch registration data from API
  const fetchRegistrations = async () => {
    try {
      setIsLoading(true);
      const { registrationType, type } = getQueryParams();

      // Call the API to get registration data with pagination
      const response = await getRegistrationsList(
        registrationType,
        type,
        currentPage,
        pagination.limit,
      );

      // Process the data for the table - adjusted for the actual response structure
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        const formattedData = response.data.data.map(formatRegistrationListItem);

        // Store the data
        setRegistrationData(formattedData);
        setFullData(formattedData);

        // Store pagination information from the response if available
        // Otherwise, calculate frontend pagination
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        } else {
          // Handle frontend pagination
          const total = formattedData.length;
          const totalPages = Math.ceil(total / pagination.limit);
          setPagination({
            total: total,
            page: currentPage,
            limit: pagination.limit,
            totalPages: totalPages > 0 ? totalPages : 1,
          });

          // Slice the data based on current page for frontend pagination
          const startIndex = (currentPage - 1) * pagination.limit;
          const endIndex = startIndex + pagination.limit;
          setRegistrationData(formattedData.slice(startIndex, endIndex));
        }
      } else {
        console.warn('Unexpected API response format:', response.data);
        setRegistrationData([]);
        setFullData([]);
      }
    } catch (error) {
      console.error('Error fetching registrations:', error);
      toast.error('Erreur lors de la récupération des enregistrements');
      setRegistrationData([]);
      setFullData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrations();
  }, [currentPage, location.search, pagination.limit]);

  // Handle search filter change
  const handleSearchFilterChange = (e) => {
    setSearchFilter(e.target.value);
  };

  // Clear search filter
  const clearSearchFilter = () => {
    setSearchFilter('');
  };

  // Apply filters to the data (client-side filtering)
  const filteredData = useMemo(() => {
    if (filters.length === 0 && !searchFilter) {
      return fullData; // Use fullData instead of registrationData
    }

    return fullData.filter((item) => {
      // Check if item passes all active filters
      const passesFilters = filters.every((filter) => {
        const itemValue = item[filter.column];

        // Handle special case for transportMode
        if (filter.column === 'transportMode') {
          return itemValue === filter.value;
        }

        // Case-insensitive string comparison for text fields
        if (typeof itemValue === 'string' && typeof filter.value === 'string') {
          return itemValue.toLowerCase() === filter.value.toLowerCase();
        }

        // Default comparison
        return itemValue === filter.value;
      });

      // Apply search filter if present - search both email and establishment name
      const passesSearchFilter =
        !searchFilter ||
        (item.email && item.email.toLowerCase().includes(searchFilter.toLowerCase())) ||
        (item.name && item.name.toLowerCase().includes(searchFilter.toLowerCase()));

      return passesFilters && passesSearchFilter;
    });
  }, [fullData, filters, searchFilter]);

  // Update pagination based on filtered data
  useEffect(() => {
    if (filters.length > 0 || searchFilter) {
      // Calculate pagination based on filtered data
      const totalFilteredItems = filteredData.length;
      const totalPages = Math.ceil(totalFilteredItems / pagination.limit);

      setPagination((prev) => ({
        ...prev,
        total: totalFilteredItems,
        totalPages: totalPages > 0 ? totalPages : 1,
      }));

      // Reset to first page when filters change
      setCurrentPage(1);
    }
  }, [filters, filteredData.length, pagination.limit, searchFilter]);

  // Extract all unique values for each filterable column when data is loaded
  useEffect(() => {
    if (fullData.length > 0) {
      const uniqueValues = {};

      filterableColumns.forEach((column) => {
        const values = [
          ...new Set(
            fullData.map((item) =>
              typeof item[column.id] === 'string' ? item[column.id] : String(item[column.id]),
            ),
          ),
        ].filter((val) => val !== '—' && val !== 'undefined' && val !== 'null' && val !== '');

        uniqueValues[column.id] = values.sort();
      });

      setUniqueValuesMap(uniqueValues);
    }
  }, [fullData]);

  // Get current items for display based on pagination
  const getCurrentItems = () => {
    // If we have filters or search filter, apply client-side pagination to filtered data
    if (filters.length > 0 || searchFilter) {
      const indexOfLastItem = currentPage * pagination.limit;
      const indexOfFirstItem = indexOfLastItem - pagination.limit;
      return filteredData.slice(indexOfFirstItem, indexOfLastItem);
    }

    // If backend pagination is not available, slice the full data
    if (!registrationData.length && fullData.length) {
      const indexOfLastItem = currentPage * pagination.limit;
      const indexOfFirstItem = indexOfLastItem - pagination.limit;
      return fullData.slice(indexOfFirstItem, indexOfLastItem);
    }

    // Otherwise, use the data from the backend (which is already paginated)
    return registrationData;
  };

  // Get display data
  const displayData = getCurrentItems();

  // Handle column selection
  const handleColumnChange = (e) => {
    setSelectedColumn(e.target.value);
    setSelectedValue('');
  };

  // Handle value selection
  const handleValueChange = (e) => {
    setSelectedValue(e.target.value);
  };

  // Add a new filter
  const handleAddFilter = () => {
    if (selectedColumn && selectedValue) {
      // Check if filter for this column already exists
      const columnExists = filters.some((filter) => filter.column === selectedColumn);

      if (columnExists) {
        // Update existing filter
        setFilters(
          filters.map((filter) =>
            filter.column === selectedColumn ? { ...filter, value: selectedValue } : filter,
          ),
        );
      } else {
        // Add new filter
        setFilters([...filters, { column: selectedColumn, value: selectedValue }]);
      }

      // Reset selection
      setSelectedColumn('');
      setSelectedValue('');
    }
  };

  // Remove a specific filter
  const handleRemoveFilter = (columnToRemove) => {
    setFilters(filters.filter((filter) => filter.column !== columnToRemove));
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFilters([]);
    setSelectedColumn('');
    setSelectedValue('');
  };

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getFilterDisplayValue = (_column, value) => value;

  // Get column label by ID
  const getColumnLabel = (columnId) => {
    return tableColumns.find((col) => col.id === columnId)?.label || columnId;
  };

  // Pagination controls
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);

    setPagination((prev) => ({
      ...prev,
      page: pageNumber,
    }));
  };

  const goToNextPage = () => {
    const nextPage = Math.min(currentPage + 1, pagination.totalPages);
    setCurrentPage(nextPage);

    setPagination((prev) => ({
      ...prev,
      page: nextPage,
    }));
  };

  const goToPrevPage = () => {
    const prevPage = Math.max(currentPage - 1, 1);
    setCurrentPage(prevPage);

    setPagination((prev) => ({
      ...prev,
      page: prevPage,
    }));
  };

  const handleLogout = () => {
    try {
      // Remove the auth token from localStorage
      localStorage.removeItem('adminToken');

      // Remove the Authorization header
      delete adminApi.defaults.headers.common['Authorization'];

      // Show success message
      toast.success(t('logout.success') || 'Déconnexion réussie');

      // Navigate to login page
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error(t('logout.error') || 'Erreur lors de la déconnexion');
    }
  };

  // Navigate to registration details
  const handleRegistrationClick = (detailUrl) => {
    navigate(detailUrl);
  };

  // Navigate back to dashboard
  const handleBackToDashboard = () => {
    navigate('/admin/dashboard');
  };

  // Function to handle scrolling the table horizontally - sync top and bottom scrollbars
  const handleScroll = (e) => {
    const { target } = e;
    const scrollContainers = document.querySelectorAll('.scroll-container');

    scrollContainers.forEach((container) => {
      if (container !== target) {
        container.scrollLeft = target.scrollLeft;
      }
    });
  };

  // Get the current registration type
  const { registrationType } = getQueryParams();

  // Handle delete confirmation
  const handleDeleteClick = async (registrationId, registrationName) => {
    // Check if admin has delete permission
    if (!hasDeletePermission) {
      toast.error('errors.PERMISSION_DENIED');
      return;
    }

    // First, open the popup with loading state
    setDeleteConfirmation({
      isOpen: true,
      registrationId,
      registrationName,
      toEmails: [],
      ccEmails: [],
    });
    setIsLoadingEmails(true);

    try {
      // Fetch full registration details to get all emails
      const { data } = await getRegistrationDetails(registrationId);
      const { inchargePersons = [] } = data;
      const contacts = inchargePersons.filter((person) => person.email?.trim());
      const primary = contacts.find((person) => !person.secondIncharge) || contacts[0];
      const secondary = contacts.find((person) => person.secondIncharge && person !== primary);
      const toEmails = primary ? [primary.email.trim()] : [];
      const ccEmails = secondary ? [secondary.email.trim()] : [];

      // Update with actual email data
      setDeleteConfirmation({
        isOpen: true,
        registrationId,
        registrationName,
        toEmails,
        ccEmails,
      });
    } catch (error) {
      console.error('Error fetching registration details:', error);
      // Fallback to basic data from table
      const registrationData = fullData.find((reg) => reg.id === registrationId);

      setDeleteConfirmation({
        isOpen: true,
        registrationId,
        registrationName,
        toEmails: registrationData?.email ? [registrationData.email] : [],
        ccEmails: [],
      });
    } finally {
      setIsLoadingEmails(false);
    }
  };

  // Handle delete confirmation close
  const handleDeleteCancel = () => {
    setDeleteConfirmation({
      isOpen: false,
      registrationId: null,
      registrationName: '',
      toEmails: [],
      ccEmails: [],
    });
    setIsLoadingEmails(false);
  };

  // Handle actual delete with email notification
  const handleDeleteConfirm = async (emailData) => {
    if (!deleteConfirmation.registrationId) return;

    try {
      setIsDeleting(true);

      // Call the actual delete API with email data
      await deleteRegistration(deleteConfirmation.registrationId, emailData);

      toast.success('Établissement supprimé et notification envoyée avec succès');
      handleDeleteCancel();

      // Refresh the registration list
      fetchRegistrations();
    } catch (error) {
      if (error.response?.data?.message === 'errors.PERMISSION_DENIED') {
        toast.error(t('errors.PERMISSION_DENIED'));
        return;
      }
      console.error('Error deleting registration:', error);
      toast.error("Erreur lors de la suppression de l'établissement");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExport = async (selectedOptions) => {
    setSelectedExportOptions(selectedOptions);

    if (!selectedOptions) {
      toast.error('No export options selected');
      return;
    }

    // Check what's selected
    const { allData, schoolTypes, otherGroupTypes } = selectedOptions;

    if (!allData && schoolTypes.length === 0 && otherGroupTypes.length === 0) {
      toast.warning('No export options selected');
      return;
    }

    try {
      setIsExportLoading(true);

      // Cancel any ongoing export request
      if (exportAbortControllerRef.current) {
        exportAbortControllerRef.current.abort();
      }

      // Create a new AbortController
      const controller = new AbortController();
      exportAbortControllerRef.current = controller;

      // Make the export request
      await exportData({ ...selectedOptions }, controller.signal);

      toast.success(t('exportData.successMessage'));
      handleCloseExportPopup(); // Close the popup after successful export
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        toast.info('Export was cancelled');
      } else {
        console.error('Export error:', error);
        toast.error('Error during export: ' + (error.message || 'Unknown error'));
      }
    } finally {
      setIsExportLoading(false);
      exportAbortControllerRef.current = null;
    }
  };

  // Dynamic column definitions based on permissions
  const getTableColumns = () => {
    const baseColumns = [
      { id: 'name', label: "Nom de l'établissement" },
      { id: 'type', label: "Type d'établissement" },
      { id: 'otherType', label: 'Autre type' },
      { id: 'postalCode', label: 'Code Postal' },
      { id: 'establishmentCity', label: "Ville de l'établissement" },
      { id: 'address', label: "Adresse de l'établissement" },
      { id: 'department', label: "Département de l'établissement" },
      { id: 'typeOfAcademy', label: "Académie de l'établissement" },
      { id: 'distanceInKm', label: 'Distance en KM' },
      { id: 'privateContract', label: 'Établissement privé SOUS CONTRAT' },
      { id: 'rep', label: 'REP?REP+' },
      { id: 'email', label: '(1) Email de contact' },
      { id: 'transportMode', label: 'Mode de transport' },
      { id: 'picnicOnSite', label: 'Pique-nique sur place' },
      { id: 'picnicTimeSlot', label: 'Créneau pique-nique' },
      { id: 'students_04_02', label: "Nombre d'élèves 04.02" },
      { id: 'students_05_02', label: "Nombre d'élèves 05.02" },
      { id: 'total_students', label: "Nombre d'élèves prévus total" },
      { id: 'accessibility', label: 'Accessibilité – Besoins spécifiques' },
    ];

    // Only add the delete action column if admin has delete permission
    if (hasDeletePermission) {
      baseColumns.push({ id: 'actions', label: 'Supprimer' });
    }

    return baseColumns;
  };

  // Get dynamic table columns
  const tableColumns = getTableColumns();
  const tableWidth = 200 + (tableColumns.length - 1) * 180;

  // Filterable columns - exclude count columns and actions
  const filterableColumns = tableColumns.filter(
    (column) =>
      !column.id.includes('students') && column.id !== 'total_students' && column.id !== 'actions',
  );

  const handleCloseExportPopup = () => {
    // Abort any ongoing export request
    if (exportAbortControllerRef.current) {
      exportAbortControllerRef.current.abort();
      exportAbortControllerRef.current = null;
      setIsExportLoading(false);
    }
    setIsExportPopupOpen(false);
  };

  // Update the export button in the popup
  const renderExportPopupFooter = () => {
    return (
      <div className="flex justify-end space-x-4">
        <button
          onClick={() => {
            // Check if there are any selected options
            const hasSelections =
              selectedExportOptions.allData ||
              selectedExportOptions.schoolTypes.length > 0 ||
              selectedExportOptions.otherGroupTypes.length > 0;

            if (hasSelections) {
              handleExport(selectedExportOptions);
            } else {
              toast.warning('Please select some export options');
            }
          }}
          disabled={isExportLoading}
          className={`${
            isExportLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-10 hover:scale-105 active:scale-95'
          } text-white py-2 px-6 rounded-xl flex items-center gap-2 font-bold transition-all duration-300`}>
          {isExportLoading ? (
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <span>{t('exportData.submitButton')}</span>
          )}
        </button>
      </div>
    );
  };

  // Get allowed sections based on query parameters
  const getExportOptions = useMemo(() => {
    const { registrationType, type } = getQueryParams();

    const result = {
      allowedSections: [],
      specificSchoolTypes: null,
      specificOtherGroupTypes: null,
      showAllDataOption: false,
    };

    // Determine what to show based on registrationType and type
    if (registrationType === 'all' && type === 'all') {
      // Show everything with the "all data" option
      result.allowedSections = ['school', 'other-group'];
      result.showAllDataOption = true;
    } else if (registrationType === 'school') {
      result.allowedSections = ['school'];

      if (type === 'all') {
        // Show all school types
        result.specificSchoolTypes = null;
      } else {
        // Show only specific school type
        result.specificSchoolTypes = [type];
      }
    } else if (registrationType === 'other-group') {
      result.allowedSections = ['other-group'];

      if (type === 'all') {
        // Show all other group types
        result.specificOtherGroupTypes = null;
      } else {
        // Show only specific other group type
        result.specificOtherGroupTypes = [type];
      }
    } else {
      // Default fallback
      result.allowedSections = ['school', 'other-group'];
    }

    return result;
  }, [location.search]); // Depend on location.search to detect query parameter changes

  // Check if admin has any group permissions
  const hasGroupPermissions = adminData?.assignedRegistrationTypes?.length > 0;

  return (
    <div>
      <div className="min-h-screen flex flex-col bg-white relative">
        <GradientStrip />

        {/* Header Section */}
        <div className="container mx-auto py-6 px-4">
          <div className="flex flex-row justify-between items-center flex-wrap gap-6">
            {/* Left side - Heading and Subheading */}
            <div className="w-auto">
              <h1 className="font-poppins text-2xl font-extrabold text-blue-10">
                PLATEFORME INSCRIPTION
              </h1>
              <p className="font-poppins text-lg text-blue-10">GROUPES GRAND EST 2026</p>
            </div>

            {/* Right side - Buttons */}
            <div className="flex items-center space-x-4">
              <button
                className="bg-blue-10 text-white hover:scale-105 active:scale-95 transition-all duration-300 p-3 px-5 rounded-xl flex items-center gap-2"
                title="Logout"
                onClick={handleLogout}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                <span className="font-bold">DECONNEXION</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 py-4">
          {/* Registration Type Header - Simple version matching provided UI */}
          <div className="flex items-center mb-6">
            <button
              onClick={handleBackToDashboard}
              className="mr-4 bg-blue-10 text-white w-8 h-8 flex items-center justify-center rounded-lg shadow-sm hover:bg-blue-20 hover:scale-105 active:scale-95 transition-all duration-300">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <div className="flex items-center">
              <div className="ml-3">
                <h3 className="font-semibold text-blue-10">
                  {getRegistrationTypeTitle(registrationType)}
                </h3>
                <p className="text-xs text-gray-600">{getSubtitle(registrationType)}</p>
              </div>
            </div>
          </div>

          {/* Table action buttons */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <div className="relative" ref={filterRef}>
                <button
                  className={`${
                    filters.length > 0
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } hover:scale-105 active:scale-95 transition-all duration-300 py-2 px-4 rounded-lg flex items-center gap-2`}
                  title="Filter"
                  onClick={() => setShowFilter(!showFilter)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                    />
                  </svg>
                  <span className="font-medium">
                    {filters.length > 0 ? `Filtres (${filters.length})` : 'Filtrer'}
                  </span>
                </button>

                {/* Filter dropdown */}
                {showFilter && (
                  <div className="absolute z-40 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-[500px]">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-medium text-gray-700">Filtrer les résultats</h3>
                      {filters.length > 0 && (
                        <button
                          className="text-sm text-red-500 hover:text-red-700"
                          onClick={handleResetFilters}>
                          Réinitialiser tous les filtres
                        </button>
                      )}
                    </div>

                    {/* Column and value selection - side by side */}
                    <div className="flex gap-3 mb-4">
                      {/* Column selection */}
                      <div className="flex-1">
                        <label
                          htmlFor="column-select"
                          className="block text-sm font-medium text-gray-700 mb-1">
                          Colonne
                        </label>
                        <select
                          id="column-select"
                          value={selectedColumn}
                          onChange={handleColumnChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm">
                          <option value="">-- Sélectionner une colonne --</option>
                          {filterableColumns
                            .filter(
                              (column) => !filters.some((filter) => filter.column === column.id),
                            )
                            .map((column) => (
                              <option key={column.id} value={column.id}>
                                {column.label}
                              </option>
                            ))}
                        </select>
                      </div>

                      {/* Value selection */}
                      <div className="flex-1">
                        <label
                          htmlFor="value-select"
                          className="block text-sm font-medium text-gray-700 mb-1">
                          Valeur
                        </label>
                        <select
                          id="value-select"
                          value={selectedValue}
                          onChange={handleValueChange}
                          disabled={!selectedColumn}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm ${!selectedColumn ? 'bg-gray-100' : ''}`}>
                          <option value="">-- Sélectionner une valeur --</option>
                          {selectedColumn &&
                            uniqueValuesMap[selectedColumn]?.map((value) => (
                              <option key={value} value={value}>
                                {value}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>

                    {/* Add filter button */}
                    <div className="flex justify-end mb-4">
                      <button
                        className={`px-4 py-2 rounded-md text-sm font-medium ${
                          selectedColumn && selectedValue
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        }`}
                        onClick={handleAddFilter}
                        disabled={!selectedColumn || !selectedValue}>
                        Ajouter le filtre
                      </button>
                    </div>

                    {/* Active filters */}
                    {filters.length > 0 && (
                      <div className="border-t border-gray-200 pt-3 mt-1">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Filtres actifs:</h4>
                        <div className="flex flex-wrap gap-2">
                          {filters.map((filter) => (
                            <div
                              key={filter.column}
                              className="group flex items-center bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-1.5 rounded-full text-sm transition-colors">
                              <span className="font-medium mr-1">
                                {getColumnLabel(filter.column)}:
                              </span>
                              <span>{getFilterDisplayValue(filter.column, filter.value)}</span>
                              <button
                                onClick={() => handleRemoveFilter(filter.column)}
                                className="ml-2 text-blue-700 group-hover:bg-red-400 group-hover:text-white p-0.5 rounded-full transition-colors">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end mt-3">
                      <button
                        className="text-sm px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
                        onClick={() => setShowFilter(false)}>
                        Fermer
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Search filter - placed next to filter button */}
              <div className="relative w-72">
                <input
                  id="search-filter"
                  type="text"
                  value={searchFilter}
                  onChange={handleSearchFilterChange}
                  placeholder="Search"
                  className="w-full h-10 px-4 py-2 pl-10 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm placeholder:text-xs placeholder:text-gray-400"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                {searchFilter && (
                  <button
                    onClick={clearSearchFilter}
                    className="absolute inset-y-0 right-0 flex items-center pr-2.5"
                    title="Effacer la recherche">
                    <div className="h-5 w-5 flex items-center justify-center bg-gray-700 bg-opacity-10 hover:bg-opacity-20 rounded-full p-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3 text-gray-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                  </button>
                )}
              </div>
            </div>

            <button
              className={`bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:scale-105 active:scale-95 transition-all duration-300 py-2 px-4 rounded-lg flex items-center gap-2 ${!hasGroupPermissions ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Export Data"
              onClick={() => setIsExportPopupOpen(true)}
              disabled={!hasGroupPermissions}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <span className="font-medium">Exporter</span>
            </button>
          </div>

          {/* Active filter badges */}
          {filters.length > 0 && (
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                {filters.map((filter) => (
                  <div
                    key={filter.column}
                    className="group flex items-center bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-1.5 rounded-full text-sm transition-colors">
                    <span className="font-medium mr-1">{getColumnLabel(filter.column)}:</span>
                    <span>{getFilterDisplayValue(filter.column, filter.value)}</span>
                    <button
                      onClick={() => handleRemoveFilter(filter.column)}
                      className="ml-2 text-blue-700 group-hover:bg-red-400 group-hover:text-white p-0.5 rounded-full transition-colors">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
                <button
                  onClick={handleResetFilters}
                  className="flex items-center bg-red-50 text-red-600 px-3 py-1.5 rounded-full text-sm hover:bg-red-100">
                  <span>Effacer tous les filtres</span>
                </button>
              </div>
            </div>
          )}

          {/* Table with double scrollbars and fixed first column */}
          <div className="relative border border-gray-200 rounded-lg overflow-hidden shadow-md">
            {/* Top scrollbar */}
            <div className="overflow-x-auto scroll-container" onScroll={handleScroll}>
              <div style={{ height: 8, width: tableWidth }} />
            </div>

            {/* Table container - removed max-height to show all 10 records without vertical scrolling */}
            <div className="overflow-x-auto scroll-container" onScroll={handleScroll}>
              <table className="table-fixed border-collapse" style={{ minWidth: tableWidth }}>
                {/* Table header */}
                <thead>
                  <tr className="bg-blue-10 text-white text-xs">
                    {tableColumns.map((column, index) => (
                      <th
                        key={column.id}
                        className={
                          index === 0
                            ? 'sticky top-0 left-0 z-30 w-[200px] p-3 border border-gray-300 bg-blue-10'
                            : 'sticky top-0 z-20 w-[180px] p-3 border border-gray-300 bg-blue-10'
                        }>
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* Table body */}
                <tbody>
                  {isLoading ? (
                    // Display loading placeholders when loading
                    Array(10)
                      .fill(null)
                      .map((_, index) => (
                        <tr
                          key={`loading-${index}`}
                          className={index % 2 === 0 ? 'bg-white' : 'bg-lightBlue-10'}>
                          <td className="sticky left-0 z-10 w-[200px] p-3 border border-gray-300 bg-blue-10">
                            <div className="h-4 bg-teal-10/80 rounded animate-pulse w-4/5"></div>
                          </td>
                          {tableColumns.slice(1).map((column) => (
                            <td key={column.id} className="p-3 border border-gray-300">
                              <div className="h-4 bg-teal-10/80 rounded animate-pulse w-3/4"></div>
                            </td>
                          ))}
                        </tr>
                      ))
                  ) : displayData.length > 0 ? (
                    displayData.map((row, index) => (
                      <tr
                        key={row.id}
                        className={`${
                          deleteConfirmation.isOpen && deleteConfirmation.registrationId === row.id
                            ? 'bg-red-50 border-red-200'
                            : index % 2 === 0
                              ? 'bg-white'
                              : 'bg-lightBlue-10'
                        } ${deleteConfirmation.isOpen && deleteConfirmation.registrationId === row.id ? 'ring-2 ring-red-200' : ''}`}>
                        <td
                          className="sticky left-0 z-10 w-[200px] p-3 border border-gray-300 text-xs truncate font-medium text-white bg-blue-10 cursor-pointer hover:bg-blue-20 transition-colors"
                          onClick={() => handleRegistrationClick(row.detailUrl)}>
                          <div className="flex items-center">
                            <span>
                              {searchFilter
                                ? highlightMatch(row.name, searchFilter, true)
                                : row.name}
                            </span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 ml-1"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                        </td>
                        {tableColumns
                          .slice(1)
                          .filter((column) => column.id !== 'actions')
                          .map((column) => (
                            <td
                              key={column.id}
                              title={String(row[column.id] ?? '')}
                              className="p-3 border border-gray-300 text-xs truncate">
                              {searchFilter && typeof row[column.id] === 'string'
                                ? highlightMatch(row[column.id], searchFilter)
                                : row[column.id]}
                            </td>
                          ))}
                        {hasDeletePermission && (
                          <td className="p-3 border border-gray-300 text-center">
                            <button
                              onClick={() => handleDeleteClick(row.id, row.name)}
                              className="inline-flex items-center justify-center w-8 h-8 text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700 rounded-lg transition-colors duration-200">
                              <FaTrash className="w-4 h-4" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={tableColumns.length} className="p-4 text-center text-gray-500">
                        {filters.length > 0
                          ? 'Aucun résultat ne correspond aux filtres sélectionnés'
                          : 'Aucun enregistrement trouvé'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Bottom scrollbar */}
            <div className="overflow-x-auto scroll-container" onScroll={handleScroll}>
              <div style={{ height: 8, width: tableWidth }} />
            </div>

            {/* Pagination */}
            <div className="bg-white px-2 py-2 border-t border-gray-200 flex items-center justify-between">
              <div className="flex-1 flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-700">
                    Affichage de{' '}
                    <span className="font-medium">
                      {pagination.total > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}
                    </span>{' '}
                    à{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>{' '}
                    sur <span className="font-medium">{pagination.total}</span> résultats
                    {filters.length > 0 && <span className="text-blue-600"> (filtrés)</span>}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={goToPrevPage}
                    disabled={currentPage === 1}
                    className={`${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                    } px-3 py-1 border border-gray-300 rounded-md text-sm`}>
                    Précédent
                  </button>

                  {Array.from({ length: Math.min(5, pagination.totalPages) }).map((_, idx) => {
                    // Create a window of page numbers centered around the current page
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (currentPage <= 3) {
                      pageNum = idx + 1;
                    } else if (currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + idx;
                    } else {
                      pageNum = currentPage - 2 + idx;
                    }

                    // Only show if the page number is valid
                    if (pageNum > 0 && pageNum <= pagination.totalPages) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => paginate(pageNum)}
                          className={`${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                          } px-3 py-1 border border-gray-300 rounded-md text-sm`}>
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  })}

                  <button
                    onClick={goToNextPage}
                    disabled={currentPage === pagination.totalPages}
                    className={`${
                      currentPage === pagination.totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                    } px-3 py-1 border border-gray-300 rounded-md text-sm`}>
                    Suivant
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Popup - Only show if admin has delete permission */}
      {hasDeletePermission && (
        <AdminPopup
          open={deleteConfirmation.isOpen}
          closePopup={isDeleting ? null : handleDeleteCancel}
          title="SUPPRIMER L'ÉTABLISSEMENT"
          variant="delete"
          customHeightClasses="h-auto"
          customWidthClasses="max-w-[1100px]">
          <DeleteConfirmation
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            isDeleting={isDeleting}
            toEmails={deleteConfirmation.toEmails}
            ccEmails={deleteConfirmation.ccEmails}
            isLoadingEmails={isLoadingEmails}
          />
        </AdminPopup>
      )}

      {/* Export Data Popup */}
      <AdminPopup
        open={isExportPopupOpen}
        closePopup={handleCloseExportPopup}
        variant="teal"
        title={t('exportData.title')}
        subTitle={t('exportData.subTitle')}
        customWidthClasses="max-w-xl"
        customHeightClasses="max-h-[90vh]"
        footer={renderExportPopupFooter()}>
        <ExportData
          onExport={setSelectedExportOptions}
          adminData={adminData}
          allowedSections={getExportOptions.allowedSections}
          specificSchoolTypes={getExportOptions.specificSchoolTypes}
          specificOtherGroupTypes={getExportOptions.specificOtherGroupTypes}
          showAllDataOption={getExportOptions.showAllDataOption}
        />
      </AdminPopup>
    </div>
  );
};

export default AdminRegistrations;
